import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup } from '@angular/forms';
import {
  OnboardingDialogAction,
  OnboardingDialogResult,
} from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { RouteDialogDirective } from '@app/+modals/route-dialog.directive';
import { handleSubmitError } from '@app/forms/form.helper';
import { selectAuthUserId } from '@app/reducers/auth/auth.selectors';
import { EmployeeForBatchPayload } from '@app/reducers/orm/employee/employee.model';
import { SharedLaddaModule } from '@app/shared/shared-ladda.module';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { EmployeeService } from '@reducers/orm/employee/employee.service';
import { ButtonComponent } from '@sb/ui/button';
import { DialogRef, SbDialogModule } from '@sb/ui/dialog';
import { ToastService } from '@sb/ui/toast';
import { combineLatestWith, concat, concatMap, EMPTY, finalize, map, Observable, of, switchMap, toArray } from 'rxjs';

import { UserGuideType } from '../../../reducers/orm/user-guide/user-guide.model';
import { SmbHospitalityOnboardingChecklistStep } from '../../../shared/onboarding/onboarding-checklist.model';
import { OnboardingChecklistService } from '../../../shared/onboarding/onboarding-checklist.service';
import { OnboardingUtils } from '../../onboarding.utils';
import { EmployeesTableComponent } from '../employees-table/employees-table.component';

export interface InviteEmployeesDialogResult extends OnboardingDialogResult {
  count: number;
  dismissed?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'invite-employees-dialog',
  imports: [
    TranslatePipe,
    SharedLaddaModule,
    ButtonComponent,
    EmployeesTableComponent,
    SbDialogModule,
    RouteDialogDirective,
  ],
  templateUrl: './invite-employees.component.html',
})
export class InviteEmployeesDialogComponent {
  dialogRef = inject<DialogRef<InviteEmployeesDialogResult>>(DialogRef, { optional: true });
  private store = inject<Store<AppState>>(Store);
  private employeeService = inject(EmployeeService);
  private onboardingUtils = inject(OnboardingUtils);
  private onboardingChecklist = inject(OnboardingChecklistService);
  private translate = inject(TranslateService);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  @ViewChild(RouteDialogDirective, { static: true })
  modal: RouteDialogDirective;

  @ViewChildren('firstNameInput', { read: ElementRef })
  firstNameInputs: QueryList<ElementRef<HTMLInputElement>>;

  @ViewChild(EmployeesTableComponent)
  employeesTable: EmployeesTableComponent;

  form = new FormGroup({
    rows: new FormArray([]),
  });

  get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  actionLoading: OnboardingDialogAction | null = null;

  authenticatedUserId = this.store.selectSignal(selectAuthUserId);

  get selectedRowsCount(): number {
    return this.form.value.rows?.reduce((acc, row) => acc + (row.selected ? 1 : 0), 0) || 0;
  }

  private dialogClosed = false;

  skip(dismissed = false): void {
    const count = this.form.value.rows.filter((row) => row.selected).length;
    this.close('secondary', count, dismissed);
  }

  submit(invite = true): void {
    if (!invite) {
      // Uncheck all rows without an email
      this.form.value.rows.forEach((row, index) => {
        if (!row.email) {
          this.rows.at(index).patchValue({ selected: false });
        }
      });
      this.employeesTable.syncAllEmailsValidity();
    }

    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    this.actionLoading = invite ? 'primary' : 'secondary';

    const { employees, teamRequests } = this.prepareEmployeeRequests();
    const saveBatch$ = this.saveBatchEmployees(employees);
    const saveTeams$ = this.saveTeams(teamRequests);
    const sendInvites$ = invite ? this.sendInvitesAndCompleteChecklist.bind(this) : null;

    void saveBatch$
      .pipe(
        concatMap((idsByEmail) => saveTeams$.pipe(map(() => idsByEmail))),
        map((idsByEmail) => this.getSelectedEmployeeIds(idsByEmail)),
        switchMap((selectedIds) => {
          if (!sendInvites$) {
            this.skip();
            return EMPTY;
          }
          return sendInvites$(selectedIds);
        }),
        finalize(() => (this.actionLoading = null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ([selectedCount]) => this.close('primary', selectedCount),
        error: (res) => this.handleSubmitError(res),
      });
  }

  private prepareEmployeeRequests() {
    const employees: EmployeeForBatchPayload[] = [];
    const teamRequests: Observable<unknown>[] = [];

    this.form.value.rows.forEach((employee) => {
      const { batchPayload, updateTeamsPayload } =
        this.onboardingUtils.mapEmployeeForBatchRequestAndUpdateTeamRequests(employee);

      employees.push(batchPayload);

      if (updateTeamsPayload) {
        teamRequests.push(this.employeeService.saveTeams(updateTeamsPayload.employeeId, updateTeamsPayload.payload));
      }
    });

    return { employees, teamRequests };
  }

  private saveBatchEmployees(employees: EmployeeForBatchPayload[]): Observable<Record<string, string>> {
    // Since there might be rows with and without id
    // and the order in the response isn't the same as the request payload
    // we identify employees by their emails (which are unique)
    if (this.form.pristine) {
      return of(this.getIdsByEmail(employees));
    }

    return this.employeeService
      .saveMany({ User: employees }, { headers: { ignoreMetaMessage: true } })
      .pipe(map((response) => this.getIdsByEmail(Object.values(response.entities.employees || {}))));
  }

  private saveTeams(teamRequests: Observable<unknown>[]): Observable<unknown[]> {
    if (teamRequests.length === 0) {
      return of([]);
    }

    return concat(...teamRequests).pipe(toArray());
  }

  private getSelectedEmployeeIds(idsByEmail: Record<string, string>): string[] {
    return this.form.value.rows.filter((row) => row.selected).map((row) => idsByEmail[row.email]) as string[];
  }

  private sendInvitesAndCompleteChecklist(selectedIds: string[]): Observable<[number, unknown]> {
    const resendInvite$ = this.employeeService.resendInvite({ ids: selectedIds }).pipe(map(() => selectedIds.length));

    const completeStep$ = this.onboardingChecklist.completeStep(
      UserGuideType.SELF_ONBOARDING_CHECKLIST,
      SmbHospitalityOnboardingChecklistStep.INVITE_TEAM,
    );

    return resendInvite$.pipe(combineLatestWith(completeStep$));
  }

  private handleSubmitError(res: unknown): void {
    const error = res as { error?: { meta?: { validation_errors?: unknown; status_message?: string } } };

    if (error?.error?.meta?.validation_errors) {
      handleSubmitError(res as HttpErrorResponse, this.rows);
      this.toast.openDanger(error.error.meta.status_message ?? this.translate.instant('Something went wrong'));
    } else {
      this.toast.openDanger(this.translate.instant('Something went wrong'));
    }
  }

  private getIdsByEmail(employees: EmployeeForBatchPayload[]) {
    return employees.reduce(
      (idsByEmail, employee) => {
        if (employee.email) {
          idsByEmail[employee.email] = employee.id;
        }

        return idsByEmail;
      },
      {} as Record<string, string>,
    );
  }

  private close(action: OnboardingDialogAction, count: number, dismissed = false) {
    if (!this.dialogClosed) {
      this.dialogClosed = true;
      this.dialogRef?.close({ action, count, dismissed });
      if (!this.dialogRef) {
        this.modal.close({ action, count, dismissed });
      }
    }
  }
}
